<template>
  <div>
    <!-- Period selector (adapted to cycle) -->
    <v-card class="mb-4" elevation="1">
      <v-card-text class="pa-3">
        <v-row align="center" justify="center">
          <v-col cols="12" sm="8" md="6">
            <div class="date-selector-wrapper">
              <v-select
                v-if="cycle === 'Daily'"
                v-model="date_history"
                :items="dailyDateOptions"
                label="Select Date"
                prepend-icon="event"
                @change="loadData"
              ></v-select>

              <v-row v-else-if="cycle === 'Weekly'">
                <v-col cols="6">
                  <v-select v-model="selectedWeek" :items="weekOptions" label="Week" @change="updateWeekly"></v-select>
                </v-col>
                <v-col cols="6">
                  <v-select v-model="selectedYear" :items="yearOptions" label="Year" @change="updateWeekly"></v-select>
                </v-col>
              </v-row>

              <v-row v-else-if="cycle === 'Monthly'">
                <v-col cols="6">
                  <v-select v-model="selectedMonth" :items="monthOptions" label="Month" @change="updateMonthly"></v-select>
                </v-col>
                <v-col cols="6">
                  <v-select v-model="selectedYear" :items="yearOptions" label="Year" @change="updateMonthly"></v-select>
                </v-col>
              </v-row>

              <v-menu v-else-if="cycle === 'Custom'" v-model="dateMenu" :close-on-content-click="false">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="date_history" label="Select Date" readonly v-bind="attrs" v-on="on"></v-text-field>
                </template>
                <v-date-picker v-model="date_history" @input="dateMenu = false; loadData()"></v-date-picker>
              </v-menu>
            </div>
          </v-col>
          <v-col cols="12" sm="4" class="text-center">
            <v-btn color="primary" @click="loadData" :loading="loading">Refresh</v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Period summary -->
    <v-card v-if="stats" class="mb-4">
      <v-card-title class="py-3">Period Statistics</v-card-title>
      <v-card-text>
        <v-progress-linear :value="percentage" height="25" :color="percentage === 100 ? 'success' : 'primary'" class="mb-2" striped>
          <strong class="white--text">{{ percentage }}%</strong>
        </v-progress-linear>
        <small>{{ formatAmount(stats.contributed_amount) }} FCFA / {{ formatAmount(stats.expected_amount) }} FCFA</small>

        <v-row class="mt-4">
          <v-col cols="6" sm="3" class="text-center">
            <div class="text-caption">Total Members</div>
            <div class="font-weight-bold">{{ stats.total_members }}</div>
          </v-col>
          <v-col cols="6" sm="3" class="text-center">
            <div class="text-caption">Contributed</div>
            <div class="font-weight-bold success--text">{{ stats.contributed_members }}</div>
          </v-col>
          <v-col cols="6" sm="3" class="text-center">
            <div class="text-caption">Missed</div>
            <div class="font-weight-bold error--text">{{ stats.missed_members }}</div>
          </v-col>
          <v-col cols="6" sm="3" class="text-center">
            <div class="text-caption">Penalties</div>
            <div class="font-weight-bold warning--text">{{ stats.penalties_count }}</div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Member list -->
    <v-card v-if="members.length">
      <v-card-title>Member Contributions</v-card-title>
      <v-list dense>
        <v-list-item v-for="m in members" :key="m._key">
          <v-list-item-icon>
            <v-icon :color="m.contributed ? 'success' : 'error'">{{ m.contributed ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ m.member_name }}</v-list-item-title>
            <v-list-item-subtitle>
              <v-chip x-small :color="m.contributed ? 'success' : 'error'">{{ m.contributed ? 'Contributed' : 'Not Contributed' }}</v-chip>
              <v-chip v-if="m.has_penalty" x-small color="warning" class="ml-2">Penalty: {{ formatAmount(m.penalty_amount) }} FCFA</v-chip>
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-card>
    <div v-else class="text-center pa-4 grey--text">No data for this period</div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'PeriodView',
  props: {
    group: Object,
    getNjangiWeekNumber: Function,
    getMondayOfWeek: Function,
    getCurrentWeek: Function,
    formatWeekString: Function
  },
  data() {
    const now = new Date()
    return {
      cycle: this.group.cycle || 'Monthly',
      date_history: '',
      selectedWeek: '01',
      selectedMonth: (now.getMonth() + 1).toString().padStart(2, '0'),
      selectedYear: now.getFullYear().toString(),
      weekOptions: Array.from({ length: 52 }, (_, i) => ({ text: `Week ${(i + 1).toString().padStart(2, '0')}`, value: (i + 1).toString().padStart(2, '0') })),
      monthOptions: [
        { text: 'Jan', value: '01' }, { text: 'Feb', value: '02' }, { text: 'Mar', value: '03' },
        { text: 'Apr', value: '04' }, { text: 'May', value: '05' }, { text: 'Jun', value: '06' },
        { text: 'Jul', value: '07' }, { text: 'Aug', value: '08' }, { text: 'Sep', value: '09' },
        { text: 'Oct', value: '10' }, { text: 'Nov', value: '11' }, { text: 'Dec', value: '12' }
      ],
      yearOptions: Array.from({ length: 5 }, (_, i) => ({ text: (now.getFullYear() - 2 + i).toString(), value: (now.getFullYear() - 2 + i).toString() })),
      dateMenu: false,
      members: [],
      stats: null,
      loading: false
    }
  },
  computed: {
    percentage() {
      if (!this.stats || !this.stats.expected_amount) return 0
      return Math.round((this.stats.contributed_amount / this.stats.expected_amount) * 100)
    }
  },
  mounted() {
    this.initializeDate()
    this.loadData()
  },
  methods: {
    initializeDate() {
      const now = new Date()
      if (this.cycle === 'Daily' || this.cycle === 'Custom') {
        this.date_history = now.toISOString().split('T')[0]
      } else if (this.cycle === 'Weekly') {
        // Use Njangi week number
        const njangiWeek = this.getNjangiWeekNumber ? this.getNjangiWeekNumber(now) : this.getWeekNumber(now)
        this.selectedWeek = njangiWeek.toString().padStart(2, '0')
        this.date_history = `${now.getFullYear()}-W${njangiWeek.toString().padStart(2, '0')}`
      } else if (this.cycle === 'Monthly') {
        this.date_history = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`
      }
    },
    // Fallback ISO week if props not passed (should not happen)
    getWeekNumber(d) {
      const date = new Date(d)
      date.setHours(0, 0, 0, 0)
      date.setDate(date.getDate() + 4 - (date.getDay() || 7))
      const yearStart = new Date(date.getFullYear(), 0, 1)
      return Math.ceil(((date - yearStart) / 86400000 + 1) / 7)
    },
    updateWeekly() {
      if (this.selectedYear && this.selectedWeek) {
        this.date_history = `${this.selectedYear}-W${this.selectedWeek}`
        this.loadData()
      }
    },
    updateMonthly() {
      if (this.selectedYear && this.selectedMonth) {
        this.date_history = `${this.selectedYear}-${this.selectedMonth}`
        this.loadData()
      }
    },
    async loadData() {
      this.loading = true
      try {
        const res = await axios.get(`/api/v1/njangi/get/user/group/members/${this.group.id}/${this.date_history}/`)
        const data = res.data
        this.members = data.group_members_contrib_info.map((m, i) => ({
          ...m,
          _key: `${m.member_id}-${Date.now()}-${i}`,
          contributed: Boolean(m.contributed),
          has_penalty: Boolean(m.has_penalty),
          penalty_amount: Number(m.penalty_amount || 0)
        }))
        this.stats = {
          total_members: this.members.length,
          contributed_members: this.members.filter(m => m.contributed).length,
          missed_members: this.members.filter(m => !m.contributed).length,
          contributed_amount: data.total_contributed || 0,
          expected_amount: data.expected_contribution || 0,
          penalties_count: this.members.filter(m => m.has_penalty).length
        }
      } catch (e) {
        console.error('Failed to load period data', e)
        this.members = []
        this.stats = null
      } finally {
        this.loading = false
      }
    },
    formatAmount(amount) {
      if (!amount && amount !== 0) return '0'
      return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
    }
  }
}
</script>

<style scoped>
.date-selector-wrapper { padding: 8px; }
</style>
